import { Component, Input } from '@angular/core';

/**
 * Program-type + platform icon pair (acceptance criterion #6 of card #887).
 * Designed to be reusable across queue rows, tile cards, and table cells.
 *
 * Example:
 *   <mkt-ws-icon-pair program="blog" platform="hubspot" />
 *   <mkt-ws-icon-pair program="paid" platform="google" />
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-icon-pair',
    template: `
<span class="ip" [title]="titleAttr">
    <span class="ip__prog">
        <i class="fa-solid {{ programIcon }}"></i>
    </span>
    <span class="ip__plat" [class]="'ip__plat--' + platform" *ngIf="platform">{{ platformLabel }}</span>
</span>
    `,
    styles: [`
        :host { display: inline-flex; }
        .ip { display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0; }
        .ip__prog { width: 20px; height: 20px; border-radius: 4px; background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); display: inline-flex; align-items: center; justify-content: center; color: var(--mj-text-secondary, #3f3f46); }
        .ip__prog i { font-size: 11px; }
        .ip__plat { width: 20px; height: 20px; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 9px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; }
        .ip__plat--google { background: #4285F4; }
        .ip__plat--linkedin { background: #0A66C2; }
        .ip__plat--hubspot { background: #FF7A59; }
        .ip__plat--buffer { background: #2C4BFF; }
        .ip__plat--rasa { background: #6B46C1; }
        .ip__plat--canva { background: #00C4CC; }
        .ip__plat--buzzsprout { background: #EC1C24; }
        .ip__plat--meta { background: #1877F2; }
        .ip__plat--youtube { background: #FF0000; }
        .ip__plat--ga4 { background: #F9AB00; color: #000; }
        .ip__plat--gemini { background: linear-gradient(135deg, #4285F4, #9b72cb); }
        .ip__plat--asae { background: #1f2937; }
        .ip__plat--internal { background: var(--mj-text-secondary, #71717a); }
    `]
})
export class IconPairComponent {
    @Input() program: ProgramType = 'internal';
    @Input() platform: PlatformKey | '' = '';
    @Input() titleAttr = '';

    get programIcon(): string {
        switch (this.program) {
            case 'blog': return 'fa-file-lines';
            case 'social': return 'fa-share-nodes';
            case 'email': return 'fa-envelope';
            case 'video': return 'fa-video';
            case 'paid': return 'fa-bullseye';
            case 'sales-enablement': return 'fa-briefcase';
            case 'lead-ops': return 'fa-user-plus';
            case 'strategy': return 'fa-compass';
            case 'experiment': return 'fa-flask';
            case 'analytics': return 'fa-chart-line';
            case 'internal': return 'fa-circle-nodes';
            default: return 'fa-circle';
        }
    }

    get platformLabel(): string {
        switch (this.platform) {
            case 'google': return 'G';
            case 'linkedin': return 'in';
            case 'hubspot': return 'H';
            case 'buffer': return 'B';
            case 'rasa': return 'R';
            case 'canva': return 'C';
            case 'buzzsprout': return 'Bz';
            case 'meta': return 'M';
            case 'youtube': return 'Yt';
            case 'ga4': return 'GA';
            case 'gemini': return 'Gm';
            case 'asae': return 'A';
            case 'internal': return 'in';
            default: return '';
        }
    }
}

export type ProgramType =
    | 'blog'
    | 'social'
    | 'email'
    | 'video'
    | 'paid'
    | 'sales-enablement'
    | 'lead-ops'
    | 'strategy'
    | 'experiment'
    | 'analytics'
    | 'internal';

export type PlatformKey =
    | 'google'
    | 'linkedin'
    | 'hubspot'
    | 'buffer'
    | 'rasa'
    | 'canva'
    | 'buzzsprout'
    | 'meta'
    | 'youtube'
    | 'ga4'
    | 'gemini'
    | 'asae'
    | 'internal';
